import React from 'react';
import { Calculator, Tag } from 'lucide-react';
import { calculateItemPrice } from '../utils/pricingUtils';
import { CartAddon, ProductOptionalComponent } from '../types';
import { useCustomerType } from '../context/CustomerTypeContext';
import { isDjBookingProduct } from '../utils/bookingFlowProducts';

interface PriceBreakdownProps {
  pricePerDay: number;
  quantity: number;
  startDate: Date | null;
  endDate: Date | null;
  upgrades?: Array<{ name: string; price: number }>;
  selectedUpgrades?: string[];
  addons?: CartAddon[];
  optionalComponents?: ProductOptionalComponent[];
  removedComponents?: string[];
  /** För DJ-bokning: fast pris för en spelning (endast DJ), inte per hyresdag */
  product?: { category?: string; slug?: string };
  className?: string;
}

const PriceBreakdown: React.FC<PriceBreakdownProps> = ({
  pricePerDay,
  quantity,
  startDate,
  endDate,
  upgrades,
  selectedUpgrades,
  addons,
  optionalComponents,
  removedComponents,
  product,
  className = ''
}) => {
  const { vatLabel } = useCustomerType();
  const djFlat = product ? isDjBookingProduct(product) : false;

  if (!startDate || !endDate) {
    return (
      <div className={`bg-gray-50 p-4 rounded-none ${className}`}>
        <div className="flex items-center mb-2">
          <Calculator className="w-5 h-5 text-gray-400 mr-2" />
          <span className="font-medium text-gray-500">Prisberäkning</span>
        </div>
        <p className="text-gray-500 text-sm">Välj datum för att se prisberäkning</p>
      </div>
    );
  }

  const calculation = calculateItemPrice(
    pricePerDay,
    quantity,
    startDate,
    endDate,
    upgrades,
    selectedUpgrades,
    addons,
    product,
    optionalComponents,
    removedComponents
  );

  return (
    <div className={`bg-gray-50 p-4 rounded-none ${className}`}>
      <div className="flex items-center mb-3">
        <Calculator className="w-5 h-5 text-purple-600 mr-2" />
        <span className="font-medium">Prisberäkning</span>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="text-xs text-gray-600">{vatLabel}</div>
        <div className="flex justify-between">
          <span>
            {djFlat
              ? 'Fast pris (DJ, en spelning):'
              : `Grundpris (${calculation.days} ${calculation.days === 1 ? 'dag' : 'dagar'}):`}
          </span>
          <span>{calculation.basePrice} kr</span>
        </div>
        {djFlat && (
          <p className="text-xs text-gray-500 -mt-1">
            Utrustning för fest ingår inte; kan bokas separat.
          </p>
        )}

        {calculation.removalsDiscount > 0 && (
          <div className="flex justify-between text-emerald-700">
            <span>Avdrag (bortval):</span>
            <span>−{calculation.removalsDiscount} kr</span>
          </div>
        )}

        {calculation.upgradesPrice > 0 && (
          <div className="flex justify-between">
            <span>Tillval:</span>
            <span>{calculation.upgradesPrice} kr</span>
          </div>
        )}

        {calculation.addonsPrice > 0 && addons && addons.length > 0 && (
          <div className="border-t pt-2 space-y-1">
            <div className="font-medium text-gray-700 mb-1">Tillägg:</div>
            {addons.map((addon) => {
              const addonTotal = addon.pricingType === 'per_day'
                ? addon.pricePerUnit * addon.quantity * calculation.days
                : addon.pricePerUnit * addon.quantity;

              return (
                <div key={addon.id} className="flex justify-between text-xs pl-2">
                  <span>
                    {addon.name} ({addon.quantity} {addon.unitLabel}
                    {addon.pricingType === 'per_day' && ` × ${calculation.days} ${calculation.days === 1 ? 'dag' : 'dagar'}`})
                  </span>
                  <span>{addonTotal} kr</span>
                </div>
              );
            })}
            <div className="flex justify-between font-medium pt-1">
              <span>Summa tillägg:</span>
              <span>{calculation.addonsPrice} kr</span>
            </div>
          </div>
        )}

        <div className="flex justify-between border-t pt-2">
          <span>Delsumma:</span>
          <span>{calculation.subtotal} kr</span>
        </div>
        
        {calculation.discountAmount > 0 && (
          <>
            <div className="flex justify-between text-green-600">
              <span className="flex items-center">
                <Tag className="w-4 h-4 mr-1" />
                Flerdagsrabatt ({calculation.discount.label}, -{calculation.discount.discountPercentage}%):
              </span>
              <span>-{calculation.discountAmount} kr</span>
            </div>
          </>
        )}
        
        <div className="flex justify-between font-bold text-lg border-t pt-2">
          <span>Totalt:</span>
          <span className="text-purple-600">{calculation.finalPrice} kr</span>
        </div>
        
        {calculation.discountAmount > 0 && (
          <div className="text-xs text-green-600 mt-2 p-2 bg-green-50 rounded">
            🎉 Du sparar {calculation.discountAmount} kr med flerdagsrabatten!
          </div>
        )}
      </div>
    </div>
  );
};

export default PriceBreakdown;